/**
 * @fileoverview Side effects for the Chat page (data fetch, scroll, cleanup).
 * @module pages/chat/useChatEffects
 */
import { useEffect } from "react";
import { logger, sanitizeError } from "../../utils/logger.js";
import {
  fetchActiveConnections,
  fetchUsers,
  fetchUserChats,
  fetchConnectionRequests,
  extractMessageTimestamp,
  maybeMarkChatAsRead,
} from "../../logic/chat/index.js";
import { SCROLL_BOTTOM_THRESHOLD_PX } from "../../utils/chatUiConstants.js";

/**
 * Runs Chat page effects: initial fetch, route sync, scroll/mark-read, upload cleanup.
 * @param {Object} ctx - Chat context
 * @param {{ connect: Function, disconnect: Function }} socket
 */
const useChatEffects = (ctx, socket) => {
  useEffect(() => {
    if (!ctx.allowed) {
      return () => {};
    }

    const fetchData = async () => {
      try {
        const userId = localStorage.getItem("userId");
        if (!userId) {
          return;
        }
        const [participants] = await Promise.all([
          fetchUserChats(
            userId,
            ctx.navigate,
            ctx.setChats,
            ctx.setChatParticipants,
          ),
          fetchActiveConnections(ctx.navigate, ctx.setActiveConnections),
          fetchConnectionRequests(
            ctx.navigate,
            ctx.setConnectionRequests,
            ctx.setConnectionRequestsLoading,
          ),
        ]);
        if (!participants) {
          await fetchUsers(ctx.navigate, ctx.setUsers, ctx.setCompanies);
        }
        socket.connect();
      } catch (error) {
        logger.error("Error during initial data fetch:", {
          err: sanitizeError(error),
        });
      } finally {
        ctx.setLoading(false);
      }
    };

    fetchData();
    return () => socket.disconnect();
  }, [ctx.allowed]);

  useEffect(() => {
    if (ctx.location.state?.activeView) {
      ctx.setActiveView(ctx.location.state.activeView);
    }
  }, [ctx.location.state]);

  useEffect(() => {
    if (ctx.chatMessages.length > 0) {
      ctx.messageEndRef.current?.scrollIntoView({
        behavior: "instant",
        block: "end",
      });
    }

    if (!ctx.selectedChat || ctx.chatMessages.length === 0) {
      return;
    }

    const currentUserId = localStorage.getItem("userId");
    const last = ctx.chatMessages[ctx.chatMessages.length - 1];
    const isIncoming = last && last.senderUserId !== currentUserId;

    const atBottom = () => {
      const el = ctx.messageEndRef.current?.parentElement;
      if (!el) {
        return true;
      }
      return (
        el.scrollHeight - el.scrollTop - el.clientHeight <
        SCROLL_BOTTOM_THRESHOLD_PX
      );
    };

    if (
      isIncoming &&
      document.visibilityState === "visible" &&
      ctx.isUserActiveRef.current &&
      atBottom()
    ) {
      ctx.setChats((prev) =>
        prev.map((c) =>
          c.PK === ctx.selectedChat.PK ? { ...c, lastMessageSeen: true } : c,
        ),
      );
      maybeMarkChatAsRead(
        ctx.selectedChat.PK,
        extractMessageTimestamp(last),
        ctx.navigate,
        ctx.isUserActiveRef,
        ctx.lastSeenMessageTimestampByChatRef,
      );
    }
  }, [ctx.chatMessages]);

  useEffect(() => {
    return () => ctx.pendingFileUploads.current.clear();
  }, []);

  useEffect(() => {
    if (ctx.selectedChat) {
      ctx.pendingFileUploads.current.clear();
    }
  }, [ctx.selectedChat?.PK]);
};

export default useChatEffects;
